import React, { useMemo } from "react";
import { RigidBody, MeshCollider } from "@react-three/rapier";
import * as THREE from "three";

const EDGE_WIDTH = 25;
const SEGMENTS_ALONG = 44;
const SEGMENTS_ACROSS = 6;
const CORNER_SEGMENTS = 6;
const NOISE_AMPLITUDE_Y = 1.0;
const NOISE_AMPLITUDE_Z = 3.5;
const INNER_NOISE_Y = 0.6;
const INNER_NOISE_Z = 1.2;

// Angle multiplier for inner slope going under the ground (higher = sharper angle)
const INNER_SLANT_MULTIPLIER = 5.0;

// Position tuning: how the edge aligns with the ground box
const TOP_Y_DIVISOR = 1.65;
const HALF_W_DIVISOR = 2.08;
const HALF_D_DIVISOR = 2.08;

// Underwater shelf — extends from slope bottom out to ocean edge
const OCEAN_EXTEND = 100; // must match Ocean.tsx
const SHELF_HORIZONTAL = 5; // flat section at slope-bottom Y before ramp
const SHELF_RAMP_LENGTH = 8; // ramp distance from slope-bottom Y up to shelf Y
const SHELF_WATER_OFFSET = 0.05; // how far below ocean surface the shelf sits
const SHELF_END = OCEAN_EXTEND + 15; // generous end to guarantee full ocean coverage

// Глибина спуску під воду: скільки одиниць нижче рівня океану опускається схил.
// Збільшуй, щоб схил йшов глибше; зменшуй, щоб закінчувався ближче до поверхні.
const SLOPE_DEPTH_BELOW_WATER = 2;

// Creates a flat underwater shelf that continues from the slope outer edge out to the ocean.
// Profile (in local slope Z): flat at slopeBottomY → ramp to shelfY → flat at shelfY to SHELF_END.
function createShelfGeometry(length: number, slopeBottomY: number, shelfY: number): THREE.BufferGeometry {
  const z0 = EDGE_WIDTH - 1; // slight overlap with slope outer edge
  const z1 = z0 + SHELF_HORIZONTAL;
  const z2 = z1 + SHELF_RAMP_LENGTH;
  const z3 = SHELF_END;

  const zProfile = [z0, z1, z2, z3];
  const yProfile = [slopeBottomY, slopeBottomY, shelfY, shelfY];
  const SEGS_X = 2;
  const cols = SEGS_X + 1;
  const rows = zProfile.length;

  const verts: number[] = [];
  const uvArr: number[] = [];
  const indices: number[] = [];

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = -length / 2 + (col / SEGS_X) * length;
      verts.push(x, yProfile[row], zProfile[row]);
      uvArr.push(col / SEGS_X, row / (rows - 1));
    }
  }

  for (let row = 0; row < rows - 1; row++) {
    for (let col = 0; col < SEGS_X; col++) {
      const a = row * cols + col;
      const b = a + 1;
      const c = a + cols;
      const d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(verts, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uvArr, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

interface GroundEdgeProps {
  groundSize: [number, number, number];
  groundPosition: [number, number, number];
  color: string;
  waterColor?: string;
}

function seededRandom(x: number, y: number): number {
  const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return n - Math.floor(n);
}

function pushVert(
  verts: number[], uvArr: number[],
  x: number, y: number, z: number,
  u: number, v: number,
): number {
  const idx = verts.length / 3;
  verts.push(x, y, z);
  uvArr.push(u, v);
  return idx;
}

function createSlopeGeometry(length: number, seed: number, slopeDepth: number, thickness: number): THREE.BufferGeometry {
  const cols = SEGMENTS_ALONG + 1;
  const rows = SEGMENTS_ACROSS + 1;

  const topPos: { x: number; y: number; z: number }[] = [];

  for (let row = 0; row < rows; row++) {
    const rowT = row / SEGMENTS_ACROSS;
    for (let col = 0; col < cols; col++) {
      const colT = col / SEGMENTS_ALONG;
      const x = -length / 2 + colT * length;

      const slopeT = rowT * rowT;
      const baseY = -slopeDepth * slopeT;
      const baseZ = EDGE_WIDTH * rowT;

      const randY = (seededRandom(col + seed, row + seed * 3) - 0.5) * 2;
      const randZ = (seededRandom(col + seed * 2, row + seed * 5) - 0.5) * 2;
      const randX = (seededRandom(col + seed * 7, row + seed * 11) - 0.5) * 2;

      const outerFactor = rowT * rowT;
      const innerFactor = (1 - rowT) * (1 - rowT);

      const noiseY = randY * (NOISE_AMPLITUDE_Y * outerFactor + INNER_NOISE_Y * innerFactor);
      const noiseZ = randZ * (NOISE_AMPLITUDE_Z * outerFactor + INNER_NOISE_Z * innerFactor);
      const xJitter = randX * (2.5 * outerFactor + 0.8 * innerFactor);

      topPos.push({
        x: x + xJitter,
        y: baseY + noiseY,
        z: baseZ + noiseZ,
      });
    }
  }

  const verts: number[] = [];
  const uvArr: number[] = [];
  const indices: number[] = [];

  const getTop = (row: number, col: number) => topPos[row * cols + col];

  // --- 1. Top surface ---
  const topStart = verts.length / 3;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const p = getTop(row, col);
      const colT = col / SEGMENTS_ALONG;
      const rowT = row / SEGMENTS_ACROSS;
      pushVert(verts, uvArr, p.x, p.y, p.z, colT * length / 10, rowT * EDGE_WIDTH / 10);
    }
  }
  for (let row = 0; row < SEGMENTS_ACROSS; row++) {
    for (let col = 0; col < SEGMENTS_ALONG; col++) {
      const a = topStart + row * cols + col;
      const b = a + 1;
      const c = a + cols;
      const d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }

  // --- 2. Bottom surface (directly below top surface) ---
  const bottomStart = verts.length / 3;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const p = getTop(row, col);
      const colT = col / SEGMENTS_ALONG;
      const rowT = row / SEGMENTS_ACROSS;
      const bottomSlantZ = (1 - rowT) * (-thickness * INNER_SLANT_MULTIPLIER);
      pushVert(verts, uvArr, p.x, p.y - thickness, p.z + bottomSlantZ, colT * length / 10, rowT * EDGE_WIDTH / 10);
    }
  }
  for (let row = 0; row < SEGMENTS_ACROSS; row++) {
    for (let col = 0; col < SEGMENTS_ALONG; col++) {
      const a = bottomStart + row * cols + col;
      const b = a + 1;
      const c = a + cols;
      const d = c + 1;
      indices.push(a, b, c, b, d, c);
    }
  }

  // --- 3. Inner wall (row=0, connects top to bottom with noise) ---
  const INNER_WALL_SEGS = 4;
  const innerWallStart = verts.length / 3;
  for (let iRow = 0; iRow <= INNER_WALL_SEGS; iRow++) {
    const t = iRow / INNER_WALL_SEGS; // 0 = top, 1 = bottom
    for (let col = 0; col < cols; col++) {
      const p = getTop(0, col);
      const colT = col / SEGMENTS_ALONG;

      const noiseFactor = t * (1 - t) * 4;
      const rY = (seededRandom(col + seed * 13, iRow + seed * 17) - 0.5) * 2;
      const rZ = (seededRandom(col + seed * 19, iRow + seed * 23) - 0.5) * 2;
      const rX = (seededRandom(col + seed * 29, iRow + seed * 31) - 0.5) * 2;

      const innerSlantZ = -thickness * INNER_SLANT_MULTIPLIER * t;
      pushVert(verts, uvArr,
        p.x + rX * 1.0 * noiseFactor,
        p.y - thickness * t + rY * 0.8 * noiseFactor,
        p.z + innerSlantZ + rZ * 0.5 * noiseFactor,
        colT * length / 10, t * thickness / 10,
      );
    }
  }
  for (let iRow = 0; iRow < INNER_WALL_SEGS; iRow++) {
    for (let col = 0; col < SEGMENTS_ALONG; col++) {
      const a = innerWallStart + iRow * cols + col;
      const b = a + 1;
      const c = a + cols;
      const d = c + 1;
      indices.push(a, b, c, b, d, c);
    }
  }

  // --- 4. Outer wall ---
  const lastRow = SEGMENTS_ACROSS;
  for (let col = 0; col < SEGMENTS_ALONG; col++) {
    const pA = getTop(lastRow, col);
    const pB = getTop(lastRow, col + 1);
    const u0 = col / SEGMENTS_ALONG;
    const u1 = (col + 1) / SEGMENTS_ALONG;

    const a = pushVert(verts, uvArr, pA.x, pA.y, pA.z, u0, 0);
    const b = pushVert(verts, uvArr, pB.x, pB.y, pB.z, u1, 0);
    const c = pushVert(verts, uvArr, pA.x, pA.y - thickness, pA.z, u0, 1);
    const d = pushVert(verts, uvArr, pB.x, pB.y - thickness, pB.z, u1, 1);
    indices.push(a, c, b, b, c, d);
  }

  // --- 5. Left side wall ---
  for (let row = 0; row < SEGMENTS_ACROSS; row++) {
    const pA = getTop(row, 0);
    const pB = getTop(row + 1, 0);
    const v0 = row / SEGMENTS_ACROSS;
    const v1 = (row + 1) / SEGMENTS_ACROSS;

    const a = pushVert(verts, uvArr, pA.x, pA.y, pA.z, 0, v0);
    const b = pushVert(verts, uvArr, pB.x, pB.y, pB.z, 0, v1);
    const c = pushVert(verts, uvArr, pA.x, pA.y - thickness, pA.z, 1, v0);
    const d = pushVert(verts, uvArr, pB.x, pB.y - thickness, pB.z, 1, v1);
    indices.push(a, c, b, b, c, d);
  }

  // --- 6. Right side wall ---
  const lastCol = SEGMENTS_ALONG;
  for (let row = 0; row < SEGMENTS_ACROSS; row++) {
    const pA = getTop(row, lastCol);
    const pB = getTop(row + 1, lastCol);
    const v0 = row / SEGMENTS_ACROSS;
    const v1 = (row + 1) / SEGMENTS_ACROSS;

    const a = pushVert(verts, uvArr, pA.x, pA.y, pA.z, 0, v0);
    const b = pushVert(verts, uvArr, pB.x, pB.y, pB.z, 0, v1);
    const c = pushVert(verts, uvArr, pA.x, pA.y - thickness, pA.z, 1, v0);
    const d = pushVert(verts, uvArr, pB.x, pB.y - thickness, pB.z, 1, v1);
    indices.push(a, b, c, b, d, c);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(verts, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uvArr, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();

  return geo;
}

function createCornerGeometry(seed: number, slopeDepth: number, thickness: number): THREE.BufferGeometry {
  const segs = CORNER_SEGMENTS;
  const rows = segs + 1;
  const cols = segs + 1;

  const verts: number[] = [];
  const uvArr: number[] = [];
  const indices: number[] = [];

  const topPos: { x: number; y: number; z: number }[] = [];

  for (let row = 0; row < rows; row++) {
    const rowT = row / segs;
    for (let col = 0; col < cols; col++) {
      const colT = col / segs;

      const x = EDGE_WIDTH * colT;
      const z = EDGE_WIDTH * rowT;

      // Distance from inner corner (0,0) determines slope
      const dist = Math.sqrt(colT * colT + rowT * rowT) / Math.SQRT2;
      const slopeT = dist * dist;
      const baseY = -slopeDepth * slopeT;

      const outerFactor = dist * dist;
      const randY = (seededRandom(col + seed, row + seed * 3) - 0.5) * 2;
      const randZ = (seededRandom(col + seed * 2, row + seed * 5) - 0.5) * 2;
      const randX = (seededRandom(col + seed * 7, row + seed * 11) - 0.5) * 2;

      topPos.push({
        x: x + randX * 1.5 * outerFactor,
        y: baseY + randY * NOISE_AMPLITUDE_Y * outerFactor,
        z: z + randZ * 1.5 * outerFactor,
      });
    }
  }

  const getTop = (row: number, col: number) => topPos[row * cols + col];

  // Top surface
  const topStart = verts.length / 3;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const p = getTop(row, col);
      pushVert(verts, uvArr, p.x, p.y, p.z, col / segs * EDGE_WIDTH / 10, row / segs * EDGE_WIDTH / 10);
    }
  }
  for (let row = 0; row < segs; row++) {
    for (let col = 0; col < segs; col++) {
      const a = topStart + row * cols + col;
      const b = a + 1;
      const c = a + cols;
      const d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }

  // Bottom surface
  const bottomStart = verts.length / 3;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const p = getTop(row, col);
      pushVert(verts, uvArr, p.x, p.y - thickness, p.z, col / segs * EDGE_WIDTH / 10, row / segs * EDGE_WIDTH / 10);
    }
  }
  for (let row = 0; row < segs; row++) {
    for (let col = 0; col < segs; col++) {
      const a = bottomStart + row * cols + col;
      const b = a + 1;
      const c = a + cols;
      const d = c + 1;
      indices.push(a, b, c, b, d, c);
    }
  }

  // Side walls - 4 edges of the corner square
  // Edge along col=last (Z side)
  for (let row = 0; row < segs; row++) {
    const pA = getTop(row, segs);
    const pB = getTop(row + 1, segs);
    const a = pushVert(verts, uvArr, pA.x, pA.y, pA.z, 0, row / segs);
    const b = pushVert(verts, uvArr, pB.x, pB.y, pB.z, 0, (row + 1) / segs);
    const c = pushVert(verts, uvArr, pA.x, pA.y - thickness, pA.z, 1, row / segs);
    const d = pushVert(verts, uvArr, pB.x, pB.y - thickness, pB.z, 1, (row + 1) / segs);
    indices.push(a, b, c, b, d, c);
  }

  // Edge along row=last (X side)
  for (let col = 0; col < segs; col++) {
    const pA = getTop(segs, col);
    const pB = getTop(segs, col + 1);
    const a = pushVert(verts, uvArr, pA.x, pA.y, pA.z, col / segs, 0);
    const b = pushVert(verts, uvArr, pB.x, pB.y, pB.z, (col + 1) / segs, 0);
    const c = pushVert(verts, uvArr, pA.x, pA.y - thickness, pA.z, col / segs, 1);
    const d = pushVert(verts, uvArr, pB.x, pB.y - thickness, pB.z, (col + 1) / segs, 1);
    indices.push(a, c, b, b, c, d);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(verts, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uvArr, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();

  return geo;
}

export default function GroundEdge({ groundSize, groundPosition, color, waterColor = "#1a4f7a" }: GroundEdgeProps) {
  const [gw, gh, gd] = groundSize;
  const [gx, gy, gz] = groundPosition;

  const topY = gy + gh / TOP_Y_DIVISOR;
  const halfW = gw / HALF_W_DIVISOR;
  const halfD = gd / HALF_D_DIVISOR;

  // Рівень води в локальному просторі схилу (Y відносно topY)
  // oceanY (world) = gy + gh/2.5 - 0.15; topY = gy + gh/TOP_Y_DIVISOR
  const waterY_local = gh * (1 / 2.5 - 1 / TOP_Y_DIVISOR) - 0.05;

  // Дно схилу: на SLOPE_DEPTH_BELOW_WATER одиниць нижче рівня води
  const slopeBottomY_local = waterY_local - SLOPE_DEPTH_BELOW_WATER;
  // Загальна глибина спуску від topY до дна схилу
  const slopeDepth = -slopeBottomY_local;

  // Полиця знаходиться трохи нижче поверхні води
  const shelfY_local = waterY_local - SHELF_WATER_OFFSET;
  const shelfWorldY = topY + shelfY_local;

  const northGeo = useMemo(() => createSlopeGeometry(gw, 1, slopeDepth, gh), [gw, gh, slopeDepth]);
  const southGeo = useMemo(() => createSlopeGeometry(gw, 2, slopeDepth, gh), [gw, gh, slopeDepth]);
  const eastGeo = useMemo(() => createSlopeGeometry(gd, 3, slopeDepth, gh), [gd, gh, slopeDepth]);
  const westGeo = useMemo(() => createSlopeGeometry(gd, 4, slopeDepth, gh), [gd, gh, slopeDepth]);

  const cornerNEGeo = useMemo(() => createCornerGeometry(10, slopeDepth, gh), [gh, slopeDepth]);
  const cornerNWGeo = useMemo(() => createCornerGeometry(20, slopeDepth, gh), [gh, slopeDepth]);
  const cornerSEGeo = useMemo(() => createCornerGeometry(30, slopeDepth, gh), [gh, slopeDepth]);
  const cornerSWGeo = useMemo(() => createCornerGeometry(40, slopeDepth, gh), [gh, slopeDepth]);

  // Shelf geometries (no physics, visual only)
  const nsShelfGeo = useMemo(() => createShelfGeometry(gw, slopeBottomY_local, shelfY_local), [gw, gh]);
  const ewShelfGeo = useMemo(() => createShelfGeometry(gd, slopeBottomY_local, shelfY_local), [gd, gh]);

  return (
    <RigidBody type="fixed" colliders={false}>
      {/* North */}
      <MeshCollider type="trimesh">
        <mesh geometry={northGeo} position={[gx, topY, gz - halfD]} rotation={[0, Math.PI, 0]} receiveShadow>
          <meshStandardMaterial color={color} roughness={1} metalness={0} />
        </mesh>
      </MeshCollider>

      {/* South */}
      <MeshCollider type="trimesh">
        <mesh geometry={southGeo} position={[gx, topY, gz + halfD]} receiveShadow>
          <meshStandardMaterial color={color} roughness={1} metalness={0} />
        </mesh>
      </MeshCollider>

      {/* East */}
      <MeshCollider type="trimesh">
        <mesh geometry={eastGeo} position={[gx + halfW, topY, gz]} rotation={[0, Math.PI / 2, 0]} receiveShadow>
          <meshStandardMaterial color={color} roughness={1} metalness={0} />
        </mesh>
      </MeshCollider>

      {/* West */}
      <MeshCollider type="trimesh">
        <mesh geometry={westGeo} position={[gx - halfW, topY, gz]} rotation={[0, -Math.PI / 2, 0]} receiveShadow>
          <meshStandardMaterial color={color} roughness={1} metalness={0} />
        </mesh>
      </MeshCollider>

      {/* Corner NE (+X, -Z) */}
      <MeshCollider type="trimesh">
        <mesh geometry={cornerNEGeo} position={[gx + halfW, topY, gz - halfD]} rotation={[0, -Math.PI / 2, 0]} receiveShadow>
          <meshStandardMaterial color={color} roughness={1} metalness={0} />
        </mesh>
      </MeshCollider>

      {/* Corner NW (-X, -Z) */}
      <MeshCollider type="trimesh">
        <mesh geometry={cornerNWGeo} position={[gx - halfW, topY, gz - halfD]} rotation={[0, Math.PI, 0]} receiveShadow>
          <meshStandardMaterial color={color} roughness={1} metalness={0} />
        </mesh>
      </MeshCollider>

      {/* Corner SE (+X, +Z) */}
      <MeshCollider type="trimesh">
        <mesh geometry={cornerSEGeo} position={[gx + halfW, topY, gz + halfD]} receiveShadow>
          <meshStandardMaterial color={color} roughness={1} metalness={0} />
        </mesh>
      </MeshCollider>

      {/* Corner SW (-X, +Z) */}
      <MeshCollider type="trimesh">
        <mesh geometry={cornerSWGeo} position={[gx - halfW, topY, gz + halfD]} rotation={[0, Math.PI / 2, 0]} receiveShadow>
          <meshStandardMaterial color={color} roughness={1} metalness={0} />
        </mesh>
      </MeshCollider>

      {/* Underwater shelf — no collider, visual only */}
      {/* Side shelves: same position/rotation as corresponding slope meshes */}
      <mesh geometry={nsShelfGeo} position={[gx, topY, gz - halfD]} rotation={[0, Math.PI, 0]}>
        <meshStandardMaterial color={waterColor} roughness={1} metalness={0} />
      </mesh>
      <mesh geometry={nsShelfGeo} position={[gx, topY, gz + halfD]}>
        <meshStandardMaterial color={waterColor} roughness={1} metalness={0} />
      </mesh>
      <mesh geometry={ewShelfGeo} position={[gx + halfW, topY, gz]} rotation={[0, Math.PI / 2, 0]}>
        <meshStandardMaterial color={waterColor} roughness={1} metalness={0} />
      </mesh>
      <mesh geometry={ewShelfGeo} position={[gx - halfW, topY, gz]} rotation={[0, -Math.PI / 2, 0]}>
        <meshStandardMaterial color={waterColor} roughness={1} metalness={0} />
      </mesh>

      {/* Corner shelves: flat quads at shelfWorldY covering outer corner areas */}
      {/* NE (+X, -Z) */}
      <mesh position={[gx + halfW + SHELF_END / 2, shelfWorldY, gz - halfD - SHELF_END / 2]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[SHELF_END, SHELF_END]} />
        <meshStandardMaterial color={waterColor} roughness={1} metalness={0} />
      </mesh>
      {/* NW (-X, -Z) */}
      <mesh position={[gx - halfW - SHELF_END / 2, shelfWorldY, gz - halfD - SHELF_END / 2]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[SHELF_END, SHELF_END]} />
        <meshStandardMaterial color={waterColor} roughness={1} metalness={0} />
      </mesh>
      {/* SE (+X, +Z) */}
      <mesh position={[gx + halfW + SHELF_END / 2, shelfWorldY, gz + halfD + SHELF_END / 2]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[SHELF_END, SHELF_END]} />
        <meshStandardMaterial color={waterColor} roughness={1} metalness={0} />
      </mesh>
      {/* SW (-X, +Z) */}
      <mesh position={[gx - halfW - SHELF_END / 2, shelfWorldY, gz + halfD + SHELF_END / 2]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[SHELF_END, SHELF_END]} />
        <meshStandardMaterial color={waterColor} roughness={1} metalness={0} />
      </mesh>
    </RigidBody>
  );
}
